'use client';

import { useState, useEffect } from 'react';
import contentManagementAPI from '@/services/api/contentManagementAPI';

const EnjoyYourTrips = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const data = await contentManagementAPI.getContentSectionByType('enjoy_your_trips');
        if (data && data.content && data.content.videos) {
          setVideos(data.content.videos);
        } else {
          // Fallback to empty array if no data
          setVideos([]);
        }
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError('Failed to load videos');
        // Set empty array as fallback
        setVideos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // Function to extract YouTube video ID and create embed URL
  const getEmbedUrl = (url) => {
    if (!url) return '';
    
    // Try to extract YouTube ID from various URL formats
    const regex = /(?:youtube\.com\/.*[v=]|youtu\.be\/)([^&\s"]{11})/;
    const match = url.match(regex);
    const videoId = match ? match[1] : null;
    
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Enjoy Your Trips</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
            Discover amazing destinations and comfortable journeys with our premium bus services.
          </p>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007f80]"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error && videos.length === 0) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Enjoy Your Trips</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-12">
            Discover amazing destinations and comfortable journeys with our premium bus services.
          </p>
          <p className="text-red-500">Failed to load videos</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Enjoy Your Trips</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover amazing destinations and comfortable journeys with our premium bus services.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video.id || video.title} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="aspect-w-16 aspect-h-9">
                  <iframe
                    src={getEmbedUrl(video.youtubeUrl)}
                    title={video.title}
                    className="w-full h-64"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{video.title}</h3>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500 text-lg">No videos available at the moment.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default EnjoyYourTrips;